/**
 * TypePriceSection — 타입별 분양가 표 (v2 목업 `.tbl`, 상세 04).
 *
 * 대표 결정(2026-09-21 #5): 공고가(타입별 최고가) 금액 표시 + 원분양가/할인가 여부 표기 필수.
 *   - 열: 타입 · 전용 · 공급 · 분양가(원분양가|할인가) · 세대  (+ lg: 인근 실거래 · 차이 — 값 있는 행이 있을 때만)
 *   - 표 위 캡션: buildPriceCaption (할인 현장이면 "원분양가 · 현재 최대 N% 할인 적용 중")
 *   - 동·호·층별 할인가 금액 표는 만들지 않는다.
 *   - v2h(2026-09-22): 목업 모바일 04 처럼 '공급' 열을 모바일에서도 표시(좁으면 가로 스크롤).
 * 순수 프레젠테이션 — 행·캡션은 호출부(SectionRenderer)가 domain/property/detailV2 로 만든다.
 */

import React from 'react';
import type { TypePriceRow } from '../../../domain/property/detailV2';
import { SectionCard } from './SectionCard';
import { TableIcon } from './SectionIcons';

export interface TypePriceSectionProps {
  title?: string;
  rows: TypePriceRow[];
  /** '원분양가' | '할인가' */
  priceLabel: string;
  caption: string;
  discounted?: boolean;
}

export function TypePriceSection({
  title = '타입별 분양가',
  rows,
  priceLabel,
  caption,
  discounted = false,
}: TypePriceSectionProps) {
  if (rows.length === 0) return null;
  const hasDeal = rows.some((r) => r.dealComparePct != null);
  const th = 'whitespace-nowrap bg-ca-surface px-2.5 py-2 text-left text-[11px] font-bold text-muted';
  const td = 'border-t border-ca-line px-2.5 py-2.5 text-[13px] font-semibold';

  return (
    <SectionCard title={title} icon={<TableIcon />}>
      <p
        className={`mb-2.5 rounded-ca-lg px-3 py-2 text-[12px] font-semibold leading-[1.5] ${
          discounted ? 'bg-accent-50 text-accent-dark' : 'bg-ca-surface text-body'
        }`}
        data-testid="type-price-caption"
      >
        {caption}
      </p>
      <div className="-mx-4 overflow-x-auto px-4">
        <table className="w-full border-collapse overflow-hidden rounded-ca-xl text-[13px]">
          <thead>
            <tr>
              <th className={th}>타입</th>
              <th className={th}>전용</th>
              <th className={th}>공급</th>
              <th className={th}>
                분양가 <span className="text-accent">({priceLabel})</span>
              </th>
              <th className={`${th} text-right`}>세대</th>
              {hasDeal && <th className={`${th} hidden lg:table-cell`}>인근 실거래</th>}
              {hasDeal && <th className={`${th} hidden lg:table-cell`}>차이</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.type}>
                <td className={`${td} text-ink`}>
                  {r.type}
                  {r.pyeong && <span className="block text-[11px] font-semibold text-accent-dark">{r.pyeong}</span>}
                </td>
                <td className={`${td} num text-body`}>{r.exclusive ?? '-'}</td>
                <td className={`${td} num text-body`}>{r.supply ?? '-'}</td>
                <td className={`${td} num whitespace-nowrap text-accent`}>
                  {r.priceMin && <span className="block">{r.priceMin} ~</span>}
                  {r.price}
                </td>
                <td className={`${td} num text-right text-body`}>{r.units != null ? r.units.toLocaleString() : '-'}</td>
                {hasDeal && <td className={`${td} num hidden text-body lg:table-cell`}>{r.recentDeal ?? '-'}</td>}
                {hasDeal && (
                  <td className={`${td} num hidden lg:table-cell`}>
                    {r.dealComparePct != null ? (
                      <span className={r.dealComparePct < 0 ? 'text-ok' : 'text-muted'}>
                        {r.dealComparePct > 0 ? '+' : ''}
                        {r.dealComparePct}%
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[11px] leading-[1.5] text-mute2">
        {rows.some((r) => r.priceMin)
          ? '입주자모집공고 기준 최저~최고 · 층·향에 따라 금액이 다릅니다'
          : '타입별 최고가 기준 · 층·향에 따라 금액이 다를 수 있습니다'}
      </p>
    </SectionCard>
  );
}

export default TypePriceSection;
